import React from 'react';
import { Link } from 'react-router-dom';
import MainNavigation from '../components/common/MainNavigation';
import FullScreenSection from '../components/common/FullScreenSection';
import HorizontalSection from '../components/common/HorizontalSection';
import ScrollReveal from '../components/common/ScrollReveal';
import TeslaButton from '../components/common/TeslaButton';
import SiteFooter from '../components/common/SiteFooter';
import StoreDownloadCta from '../components/common/StoreDownloadCta';
import '../styles/MobileAppPromo.css';

const HomePage: React.FC = () => {
  return (
    <>
      {/* Main Navigation */}
      <MainNavigation variant="transparent" />
      
      <div className="scroll-snap-container">
        {/* Hero Section */}
        <FullScreenSection
          title="Beauty, Simplified"
          subtitle="Book appointments with top stylists in your area with just a few taps"
          backgroundImage="https://images.unsplash.com/photo-1562322140-8baeececf3df?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80"
          backgroundOverlay={true}
          textColor="light"
          scrollIndicator={true}
          className="section--lg"
        >
          <div className="hero-cta-section">
            <div className="cta-buttons">
              <Link to="/register" style={{ textDecoration: 'none' }}>
                <TeslaButton variant="primary" size="lg">
                  Get Started - It's Free
                </TeslaButton>
              </Link>
              <Link 
                to="/login" 
                className="btn btn--secondary btn--lg"
                style={{ 
                  cursor: 'pointer', 
                  border: 'none',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center' 
                }}
              >
                Sign In
              </Link>
            </div>
            
            <div className="mobile-app-promo">
              <p className="app-promo-text">
                ✨ Get the full experience with our mobile app
              </p>
              <div className="app-store-buttons">
                <StoreDownloadCta store="ios" variant="hero" />
                <StoreDownloadCta store="android" variant="hero" />
              </div>
            </div>
          </div>
        </FullScreenSection>

        {/* Services Section */}
        <section id="services">
          <HorizontalSection
            title="Find Your Perfect Stylist"
            subtitle="DISCOVER"
            description="Browse through our curated selection of top stylists in your area. Filter by specialization, price range, and availability to find the perfect match for your needs."
            imageSrc="https://images.unsplash.com/photo-1560066984-138dadb4c035?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80"
            imageAlt="Woman browsing through stylist profiles"
            imagePlaceholder="https://images.unsplash.com/photo-1560066984-138dadb4c035?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80"
            ctaText="Browse Stylists"
            onCtaClick={() => {}}
          />
        </section>

        {/* How It Works Section */}
        <section id="how-it-works">
          <HorizontalSection
            title="Book With Confidence"
            subtitle="CONVENIENCE"
            description="Schedule appointments in seconds, not minutes. Our streamlined booking process lets you select your preferred date, time, and service with ease. No phone calls, no waiting."
            imageSrc="https://images.pexels.com/photos/7697350/pexels-photo-7697350.jpeg?auto=compress&cs=tinysrgb&w=1950"
            imageAlt="African lady braiding hair in a professional salon"
            imagePlaceholder="https://images.pexels.com/photos/7697350/pexels-photo-7697350.jpeg?auto=compress&cs=tinysrgb&w=100"
            reverse={true}
            ctaText="Learn More"
            onCtaClick={() => {}}
          />

          <HorizontalSection
            title="Manage Your Appointments"
            subtitle="CONTROL"
            description="Keep track of upcoming appointments, reschedule with a tap, and review past services. Your beauty journey, simplified and organized in one place."
            imageSrc="https://images.unsplash.com/photo-1516975080664-ed2fc6a32937?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80"
            imageAlt="Woman reviewing appointments on mobile"
            imagePlaceholder="https://images.unsplash.com/photo-1516975080664-ed2fc6a32937?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80"
            ctaText="See How It Works"
            onCtaClick={() => {}}
          />
          
          {/* Mobile Service Section */}
          <HorizontalSection
            title="We Can Come to You"
            subtitle="MOBILE SERVICE"
            description="No time to visit a salon? Our mobile stylists bring the salon experience to your home, office, or event. Enjoy professional beauty services in the comfort and convenience of your preferred location."
            imageSrc="https://images.unsplash.com/photo-1562516710-38a6fa229b23?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80"
            imageAlt="Stylist providing mobile hair service at client's home"
            imagePlaceholder="https://images.unsplash.com/photo-1562516710-38a6fa229b23?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80"
            reverse={true}
            ctaText="Learn About Mobile Services"
            onCtaClick={() => {}}
          />
        </section>
        
        {/* Video Hero Section */}
        <div className="salon-video-section">
          <div className="salon-video-container">
            <div className="video-overlay">
              <div className="video-content">
                <h2 className="video-title">Experience the Art of Beauty</h2>
                <p className="video-description">
                  See our talented stylists in action, creating beautiful transformations every day
                </p>
              </div>
            </div>
            <div className="responsive-iframe-container">
              <iframe 
                className="responsive-iframe"
                src="https://www.youtube.com/embed/5VvEe0nlx9w?autoplay=1&mute=1&controls=0&loop=1&playlist=5VvEe0nlx9w"
                title="Hair Salon Professional Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>

        {/* About Section */}
        <section id="about" className="section section--center section--light">
          <div className="container">
            <ScrollReveal animation="fade-in-up">
              <h2 className="text-heading-2 mb-12 text-center">What Our Clients Say</h2>
            </ScrollReveal>
            
            <div className="grid grid--3-col">
              {testimonials.map((testimonial, index) => (
                <ScrollReveal 
                  key={index} 
                  animation="fade-in-up" 
                  delay={200 * index}
                >
                  <div className="tesla-card p-8">
                    <div className="mb-4 text-accent">
                      {/* Star rating */}
                      <div className="d-flex">
                        {[...Array(5)].map((_, i) => (
                          <span key={i} className={i < testimonial.rating ? "text-warning" : "text-gray-40"}>★</span>
                        ))}
                      </div>
                    </div>
                    <p className="mb-6">{testimonial.text}</p>
                    <div className="d-flex align-items-center">
                      <div className="rounded-full overflow-hidden mr-4" style={{ width: '48px', height: '48px' }}>
                        <img src={testimonial.avatar} alt={testimonial.name} />
                      </div>
                      <div>
                        <div className="fw-medium">{testimonial.name}</div>
                        <div className="text-primary-300 text-body-small">{testimonial.location}</div>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* For Stylists Section */}
        <section className="section section--light">
          <div className="container">
            <ScrollReveal animation="fade-in-up">
              <div className="text-center mb-16">
                <h2 className="text-heading-2 mb-6">For Beauty Professionals</h2>
                <p className="text-body-large text-gray-70 max-w-3xl mx-auto">
                  Join our platform and grow your beauty business with powerful tools designed for modern stylists.
                </p>
              </div>
            </ScrollReveal>
            
            <div className="grid grid--3-col mb-12">
              <ScrollReveal animation="fade-in-up" delay={100}>
                <div className="feature-card">
                  <div className="feature-icon">📅</div>
                  <h3>Smart Scheduling</h3>
                  <p>Manage your availability and let clients book instantly. No more phone tag or double bookings.</p>
                </div>
              </ScrollReveal>
              
              <ScrollReveal animation="fade-in-up" delay={200}>
                <div className="feature-card">
                  <div className="feature-icon">💳</div>
                  <h3>Secure Payments</h3>
                  <p>Get paid automatically with Stripe. Booking guarantees secure bookings and full payment on completion.</p>
                </div>
              </ScrollReveal>
              
              <ScrollReveal animation="fade-in-up" delay={300}>
                <div className="feature-card">
                  <div className="feature-icon">📱</div>
                  <h3>Mobile Services</h3>
                  <p>Offer mobile services and expand your reach. Manage on-location appointments with GPS check-in.</p>
                </div>
              </ScrollReveal>
            </div>
            
            <div className="text-center">
              <ScrollReveal animation="fade-in-up">
                <Link to="/register" style={{ textDecoration: 'none' }}>
                  <TeslaButton variant="primary" size="lg">
                    Join as a Stylist
                  </TeslaButton>
                </Link>
                <p className="mt-4 text-gray-60">
                  Start with our web platform, then download the mobile app for the full experience
                </p>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section section--dark">
          <div className="container text-center">
            <ScrollReveal animation="fade-in-up">
              <h2 className="text-heading-2 mb-6 text-white">Ready to Transform Your Beauty Routine?</h2>
              <p className="text-body-large mb-8 text-gray-40">
                Join thousands of clients who've simplified their beauty appointments with LookBel.
              </p>
              <div className="d-flex justify-content-center gap-4">
                <Link to="/register" style={{ textDecoration: 'none' }}>
                  <TeslaButton variant="primary" size="lg">
                    Get Started Free
                  </TeslaButton>
                </Link>
                <Link 
                  to="/login" 
                  className="btn btn--outline btn--lg"
                  style={{ 
                    cursor: 'pointer',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center' 
                  }}
                >
                  Sign In
                </Link>
              </div>
              <p className="mt-6 text-gray-50 text-body-small">
                🚀 Your account works on both web and mobile • No credit card required
              </p>
            </ScrollReveal>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact">
          <SiteFooter />
        </section>
      </div>
    </>
  );
};

// Mock testimonials data
const testimonials = [
  {
    name: "Sarah J.",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80",
    location: "New York, NY",
    rating: 5,
    text: "LookBel has completely changed how I book my hair appointments. No more waiting on hold or playing phone tag with the salon. Just a few taps and I'm done!"
  },
  {
    name: "Michael T.",
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80",
    location: "Los Angeles, CA",
    rating: 5,
    text: "As someone who's always on the go, I appreciate how easy it is to reschedule or cancel appointments when my plans change. The interface is incredibly intuitive."
  },
  {
    name: "Jessica M.",
    avatar: "https://images.unsplash.com/photo-1532910404247-7ee9488d7292?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80",
    location: "Chicago, IL",
    rating: 4,
    text: "I discovered my new favorite stylist through LookBel's recommendations. The detailed profiles and portfolios helped me find someone who perfectly matched my style."
  }
];

export default HomePage;